import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import EmptyState from "@/components/feature/EmptyState";
import ResultCard from "@/pages/search/components/ResultCard";
import { getHotList, searchCatalog } from "@/lib/catalog";

const hotKeywords = ["科幻", "悬疑", "爱情", "古装", "中国大陆", "美国"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);

  useSeo({
    title: q ? `“${q}”的搜索结果 - 金桔影院` : "搜索影视 - 金桔影院",
    description: q
      ? `金桔影院为你找到与“${q}”相关的电影与电视剧推荐，查看作品详情、评分与看点介绍。`
      : "在金桔影院搜索最新电影与电视剧，快速找到你想看的影视作品。",
    keywords: `金桔影院搜索,${q || "影视搜索"},电影搜索,电视剧搜索`,
    canonicalPath: "/search",
  });

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = input.trim();
      if (trimmed === q) return;
      setSearchParams(trimmed ? { q: trimmed } : {}, { replace: true });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [input, q, setSearchParams]);

  const results = q ? searchCatalog(q) : [];
  const hot = getHotList(4);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    setSearchParams(trimmed ? { q: trimmed } : {}, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-8xl px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-semibold text-primary-600">搜索</span>
      </nav>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h1 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          {q ? (
            <>
              搜索 “
              <span className="text-primary-600">{q}</span>
              ” 的结果
            </>
          ) : (
            "搜索影视作品"
          )}
        </h1>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片名称、题材、地区等关键词"
              className="w-full bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput("")}
                className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-700"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => setInput(word)}
              className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {q && results.length > 0 ? (
          <>
            <p className="mb-4 text-sm text-foreground-600">
              共找到 <span className="font-bold text-primary-600">{results.length}</span> 个与“
              <span className="font-semibold text-foreground-900">{q}</span>”相关的结果
            </p>
            <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2">
              {results.map((item) => (
                <ResultCard key={item.id} item={item} keyword={q} />
              ))}
            </div>
          </>
        ) : q ? (
          <EmptyState
            title={`没有找到与“${q}”相关的作品`}
            description="换个关键词试试，或者看看下面的热门推荐。"
            resetLabel="清空搜索"
            onReset={() => setInput("")}
          />
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-accent-500">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              输入关键词开始搜索
            </h3>
            <p className="mt-2 text-sm text-foreground-500">
              支持按影片名称、题材类型、地区等关键词查找影视作品。
            </p>
          </div>
        )}
      </div>

      {(!q || results.length === 0) && (
        <div className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-fire-line text-base"></i>
            </span>
            大家都在看
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2">
            {hot.map((item) => (
              <ResultCard key={item.id} item={item} keyword="" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}